import { useTranslation } from 'react-i18next';
import { Box } from '@mui/material';

import { Typography } from '@zendesk/forethought-elements';
import { toConversationChannel } from '../utils';
import { DeepLink } from './DeepLink';
import type { FontVariant } from './fontVariant';
import {
  ErrorQueryEvent,
  MissingContextVariablePayload,
} from 'src/types/ErrorEventTypes';

// Preserve empty entries so names stay aligned with their corresponding IDs.
const toStringList = (value: unknown): string[] => {
  return Array.isArray(value)
    ? value.map(item => (typeof item === 'string' ? item.trim() : ''))
    : [];
};

export const MissingContextVariableFix = ({
  channel,
  fontVariant = 'font16',
  payload,
}: {
  channel: ErrorQueryEvent['channel'];
  fontVariant?: FontVariant;
  payload: MissingContextVariablePayload | null;
}) => {
  const { t } = useTranslation('events');
  const failedCvIds = toStringList(payload?.failed_cv_ids);
  const failedCvNames = toStringList(payload?.failed_cvs);
  const missingContextVariables = Array.from(
    { length: Math.max(failedCvIds.length, failedCvNames.length) },
    (_, index) => ({ id: failedCvIds[index], name: failedCvNames[index] }),
  ).filter(({ id, name }) => id || name);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
      }}
    >
      {missingContextVariables.length > 0 && (
        <Box>
          <Typography variant={fontVariant}>
            {t('missingContextVariableFix.missingVariables', {
              count: missingContextVariables.length,
            })}
          </Typography>
          <Box
            component='ul'
            sx={{ m: 0, overflowWrap: 'anywhere', pl: '20px' }}
          >
            {missingContextVariables.map(({ id, name }, index) => (
              <li key={`${id || name}-${index}`}>
                <Typography variant={fontVariant}>
                  <strong>{name || id}</strong>
                  {name && id && name !== id && (
                    <Box component='span' sx={{ opacity: 0.7 }}>
                      {t('missingContextVariableFix.variableId', { id })}
                    </Box>
                  )}
                </Typography>
              </li>
            ))}
          </Box>
        </Box>
      )}
      <Typography variant={fontVariant}>
        {t('missingContextVariableFix.intro')}
      </Typography>
      <Typography variant={fontVariant}>
        {t('missingContextVariableFix.method1')}
      </Typography>
      <Typography variant={fontVariant}>
        {t('missingContextVariableFix.method2')}
      </Typography>
      <Typography variant={fontVariant}>
        {t('missingContextVariableFix.method3')}
      </Typography>
      {payload?.step_id && payload?.workflow_id && (
        <Box sx={{ mt: 1 }}>
          <DeepLink
            channel={toConversationChannel(channel)}
            stepId={payload.step_id}
            workflowId={payload.workflow_id}
          />
        </Box>
      )}
    </Box>
  );
};
